import React, { useEffect, useState } from "react";
import { StatusCode } from "api/protocols";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import Page from "components/page/Page";
import { useAppDispatch } from "hooks/redux";
import { useHistory, useParams } from "react-router";
import { showError, showSuccess } from "redux/actions/ui";
import Popup from "components/popup/Popup";
import { isEqualWith } from "lodash";
import { NotificationType } from "components/notificationBar/NotificationBar";

import { IKitchenCourse } from "./KitchenCourse";
import { useTranslation, Trans } from "react-i18next";
import {
  DeleteKitchenMeal,
  GetKitchenMeals,
  IKitchenMeal,
  PutKitchenMeal,
} from "api/rpc/2024-04/facilityAdmin/facility/kitchen/meals";
interface IState {
  title: string;
  course: IKitchenMeal;
  deletePopup: boolean;
}

interface IParams {
  id: string;
}

export default function KitchenCourseEdit(props: any) {
  const [state, setState] = useState<IState>({
    title: "",
    course: null,
    deletePopup: false,
  });
  const [titleBeforeChanges, setTitleBeforeChanges] = useState<string>(undefined);
  const { id } = useParams<IParams>();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  useEffect(() => {
    void loadKitchenCourse();
  }, []);

  async function loadKitchenCourse() {
    const mealRes = await GetKitchenMeals({ id: Number(id) }, true);
    if (mealRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting menu group")));
      history.push("/admin/settings/courses");
      return;
    }
    if (mealRes?.data?.length === 1) {
      setState(prevState => ({ ...prevState, course: mealRes?.data[0], title: mealRes?.data[0]?.title }));
    } else {
      dispatch(showError(t("Error getting menu group")));
      history.push("/admin/settings/courses");
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  async function updateKitchenCourse() {
    const updateMealRes = await PutKitchenMeal({ id: Number(id), title: state?.title }, true);
    if (updateMealRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating menu group")));
      return;
    }
    dispatch(showSuccess(t("Successfully updated menu group")));
    history.push("/admin/settings/courses");
  }

  async function handleDeleteCourse() {
    const deleteRes = await DeleteKitchenMeal({ id: Number(id) }, true);
    if (deleteRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting menu group")));
      return;
    }
    dispatch(showSuccess(t("Successfully deleted menu group")));
    history.push("/admin/settings/courses");
  }

  const primaryAction = {
    content: t("Delete"),
    action: () => setState(prevState => ({ ...prevState, deletePopup: true })),
  };

  function unsavedChangesExist() {
    if (titleBeforeChanges === undefined) {
      if (state?.course) {
        setTitleBeforeChanges(state?.title);
      }
      return false;
    }

    return !isEqualWith(titleBeforeChanges, state?.title);
  }

  function cancelUnsavedChanges() {
    setState(prevState => ({ ...prevState, title: titleBeforeChanges }));
  }

  return (
    <Page
      title={`${t("Edit Menu Group:")} ${state?.course?.title ?? ""}`}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Menu Groups"),
          url: "/admin/settings/courses",
        },
      ]}
      primaryAction={primaryAction}
      narrow
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: updateKitchenCourse,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Card>
        <Card.Section>
          <Input
            label={t("Title")}
            placeholder={t("Title")}
            id="title"
            value={state?.title ?? ""}
            onChange={handleInputChange}
          />
        </Card.Section>
      </Card>

      <Popup
        open={state?.deletePopup}
        type="warning"
        title={t("Delete menu group?")}
        description={t("Are you sure you want to delete this menu group?")}
        onCancel={() => setState(prevState => ({ ...prevState, deletePopup: false }))}
        onOk={handleDeleteCourse}
      />
    </Page>
  );
}
